import { useMutation, useQuery, useReactiveVar } from "@apollo/client";
import { useEffect, useState } from "react";
import {
  GetAskAiChatSessionQueryQuery,
  GetAskAiChatSessionQueryQueryVariables,
  GetOrCreateChatSessionMutation,
  GetOrCreateChatSessionMutationVariables,
} from "types/graphql-schema";

import AskAI from "@apps/ask-ai/ask-ai";
import { ChatSessionContext } from "@apps/ask-ai/ask-ai-context";
import getAskAiSessionQuery from "@apps/ask-ai/graphql/get-ask-ai-session-query";
import getOrCreateChatSessionMutation from "@apps/ask-ai/graphql/get-or-create-chat-session-mutation";
import Loading from "@components/loading/loading";
import Tooltip from "@components/tooltip/tooltip";
import { classNames } from "@helpers/css";

import getChatSessionsForAssessmentQuery from "../graphql/get-chat-sessions-for-assessment-query";
import { assessmentContextSidebarVar } from "./context-sidebar/context-sidebar-context";

const AssessmentAskAI = ({
  assessmentId,
  chatSessionId,
  startedChatSession,
  onChangeChatSessionId,
  onStartChatSession,
}: {
  assessmentId?: number;
  startedChatSession: boolean;
  chatSessionId: number | null;
  onChangeChatSessionId: (chatSessionId: number | null) => void;
  onStartChatSession: () => void;
}) => {
  const assessmentContextSidebar = useReactiveVar(assessmentContextSidebarVar);
  const questionId = assessmentContextSidebar?.questionId;

  const [hasPrompted, setHasPrompted] = useState(false);
  const [
    currentSuggestedAssessmentResponseId,
    setCurrentSuggestedAssessmentResponseId,
  ] = useState<string | null>(null);

  const [createChatSession, { loading: loadingCreateSession }] = useMutation<
    GetOrCreateChatSessionMutation,
    GetOrCreateChatSessionMutationVariables
  >(getOrCreateChatSessionMutation);

  const handleCreateAskAIChatSession = (assessmentQuestionId: number) => {
    if (loadingCreateSession) return;
    onStartChatSession();
    createChatSession({
      variables: {
        assessmentId,
        assessmentQuestionId,
        seedWithSuggestedAssessmentAnswer: true,
      },
      refetchQueries: [getChatSessionsForAssessmentQuery],
      onCompleted: (response) => {
        const newChatSession = response?.getOrCreateChatbotSession?.chatSession;
        if (newChatSession) {
          onChangeChatSessionId(newChatSession.id);
        }
      },
    });
  };

  // We'll need to rework this once we have a way to create or retrieve a chat session for an assessment question.
  //
  useEffect(() => {
    if (waffle.flag_is_active("review-context-sidebar")) {
      return;
    }
    if (!waffle.flag_is_active("generated-context-in-ask-ai")) {
      return;
    }
    if (questionId && !hasPrompted) {
      setHasPrompted(true);
    }
    if (questionId) {
      handleCreateAskAIChatSession(questionId);
    }
  }, [questionId]);

  useEffect(() => {
    if (!questionId) {
      setCurrentSuggestedAssessmentResponseId(null);
    }
  }, [questionId]);

  const { data: cachedChatSession } = useQuery<
    GetAskAiChatSessionQueryQuery,
    GetAskAiChatSessionQueryQueryVariables
  >(getAskAiSessionQuery, {
    variables: { chatSessionId: chatSessionId || 0 },
    fetchPolicy: "cache-only",
    skip: !chatSessionId,
  });
  const assessmentQuestionTitle =
    cachedChatSession?.chatSession?.assessmentQuestion?.title;

  return loadingCreateSession ? (
    <Loading className="p-6">Asking AI for suggested responses...</Loading>
  ) : (
    <ChatSessionContext.Provider
      value={{
        assessmentId,
        assessmentQuestionId: questionId,
        currentSuggestedAssessmentResponseId,
        setCurrentSuggestedAssessmentResponseId,
      }}
    >
      <div className={classNames("flex-1 flex flex-col")}>
        {assessmentQuestionTitle && (
          <Tooltip text={assessmentQuestionTitle}>
            <div className="bg-gray-50 border-b px-6 py-4 flex items-center gap-2">
              <Tooltip text="Context generation is a new feature currently in beta. We are actively improving the experience!">
                <div
                  data-state="In progress"
                  className="px-2 py-0.5 bg-blue-800 rounded-[14px] text-white text-xs"
                >
                  Beta
                </div>
              </Tooltip>
              <div className="truncate flex-1">{assessmentQuestionTitle}</div>
            </div>
          </Tooltip>
        )}
        <AskAI
          isInSidebar={true}
          chatSessionId={chatSessionId}
          onChangeChatSessionId={onChangeChatSessionId}
          onStartChatSession={onStartChatSession}
          isFullScreen={!!chatSessionId || startedChatSession}
          className="px-6 pb-6"
          totalYPaddingAroundContainer={assessmentQuestionTitle ? 170 : 110}
        />
      </div>
    </ChatSessionContext.Provider>
  );
};

export default AssessmentAskAI;
